import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, CheckCircle2, CloudOff, Loader2 } from 'lucide-react';
import { annoncer } from './Retours';

/* ═══════════════════════════════════════════════════════════════════════
   5. FORMULAIRES ET INTERACTIONS

   - `MessageChamp` : l'erreur (ou la confirmation) SOUS le champ concerné,
     reliée par `aria-describedby` — la couleur seule ne dit rien.
   - `useModifications` : modifié / non enregistré, avec l'avertissement du
     navigateur si l'on quitte la page.
   - `IndicateurEnregistrement` : Non enregistré · Enregistrement… ·
     Enregistré · Échec — pour les enregistrements automatiques.
   - `useCopie` : copié / copie impossible.
   ═══════════════════════════════════════════════════════════════════════ */

export function MessageChamp({ id, erreur, succes, aide }) {
  if (erreur) {
    return <p id={id} role="alert" className="mt-1.5 mb-0 flex items-start gap-1.5 text-[12.5px] text-[var(--alerte)]"><AlertCircle className="w-3.5 h-3.5 shrink-0 mt-[2px]" />{erreur}</p>;
  }
  if (succes) {
    return <p id={id} className="mt-1.5 mb-0 flex items-start gap-1.5 text-[12.5px] text-[var(--accent-texte)]"><CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-[2px]" />{succes}</p>;
  }
  if (aide) return <p id={id} className="mt-1.5 mb-0 text-[12px] text-[var(--text-muted)]">{aide}</p>;
  return null;
}

/** Suivre si des valeurs ont changé depuis leur état initial. */
export function useModifications(valeurs, initiales, { avertirEnQuittant = true } = {}) {
  const modifie = JSON.stringify(valeurs) !== JSON.stringify(initiales);
  useEffect(() => {
    if (!avertirEnQuittant || !modifie) return undefined;
    const retenir = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', retenir);
    return () => window.removeEventListener('beforeunload', retenir);
  }, [modifie, avertirEnQuittant]);
  return modifie;
}

/** Non enregistré · Enregistrement… · Enregistré · Échec. */
export function IndicateurEnregistrement({ etat, enregistreLe }) {
  const contenu = {
    modifie: [AlertCircle, 'Modifications non enregistrées', 'text-[var(--attention)]'],
    enregistrement: [Loader2, 'Enregistrement…', 'text-[var(--text-muted)]'],
    enregistre: [Check, enregistreLe ? `Enregistré à ${new Date(enregistreLe).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : 'Enregistré', 'text-[var(--accent-texte)]'],
    echec: [CloudOff, 'Échec de l’enregistrement', 'text-[var(--alerte)]'],
  }[etat];
  if (!contenu) return null;
  const [Icone, texte, couleur] = contenu;
  return (
    <span className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${couleur}`} role="status" aria-live="polite">
      <Icone className={`w-3.5 h-3.5 ${etat === 'enregistrement' ? 'animate-spin' : ''}`} aria-hidden="true" />{texte}
    </span>
  );
}

/** Enregistrement automatique après une pause de saisie. */
export function useEnregistrementAuto(valeurs, enregistrer, { delai = 1200, actif = true } = {}) {
  const [etat, setEtat] = useState('inactif');
  const [enregistreLe, setEnregistreLe] = useState(null);
  const premier = useRef(true);
  const cle = JSON.stringify(valeurs);
  useEffect(() => {
    if (!actif) return undefined;
    if (premier.current) { premier.current = false; return undefined; }
    setEtat('modifie');
    const t = setTimeout(async () => {
      setEtat('enregistrement');
      try { await enregistrer(valeurs); setEtat('enregistre'); setEnregistreLe(Date.now()); } catch { setEtat('echec'); }
    }, delai);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cle, actif]);
  return { etat, enregistreLe };
}

/** Copier dans le presse-papiers, avec son retour. */
export function useCopie() {
  const [copie, setCopie] = useState(false);
  const copier = useCallback(async (texte, message = 'Copié dans le presse-papiers.') => {
    try {
      await navigator.clipboard.writeText(texte);
      setCopie(true);
      annoncer(message, { ton: 'succes', duree: 2500 });
      setTimeout(() => setCopie(false), 2000);
      return true;
    } catch {
      annoncer('La copie a échoué : sélectionnez le texte et copiez-le à la main.', { ton: 'erreur' });
      return false;
    }
  }, []);
  return { copie, copier };
}
